import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import AppTheme from '../../themes/shared-theme/AppTheme';
import ColorModeSelect from '../../themes/shared-theme/ColorModeSelect';
import { setUserAuthenticated } from '../../helpers/authSession';
import { authenticate } from '../../services/rulesEngineSession';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  maxWidth: '360px',
  padding: theme.spacing(3),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  alignItems: 'center',
  padding: theme.spacing(2),
  paddingTop: theme.spacing(6),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

export default function SignIn(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [identifierError, setIdentifierError] = React.useState(false);
  const [identifierErrorMessage, setIdentifierErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateInputs()) {
      return;
    }

    const data = new FormData(event.currentTarget);
    const identifier = data.get('identifier');
    const password = data.get('password');

    setIsSubmitting(true);

    try {
      const authResult = await authenticate({
        email: String(identifier || '').trim(),
        password: String(password || ''),
      });

      setPasswordError(false);
      setPasswordErrorMessage('');
      setUserAuthenticated(
        authResult?.roles?.length ? authResult.roles : authResult?.role,
        authResult?.clientId || String(identifier || '').trim(),
        authResult?.claims?.organization_ids || [],
        authResult?.claims?.permissions || [],
      );

      const targetPath = location.state?.from?.pathname || '/dashboard';
      navigate(targetPath, { replace: true });
    } catch (error) {
      setPasswordError(true);
      const status = error?.response?.status;

      if (status === 401) {
        setPasswordErrorMessage('Credenciais inválidas.');
      } else if (status === 429) {
        setPasswordErrorMessage('Muitas tentativas. Tente novamente em instantes.');
      } else {
        setPasswordErrorMessage('Nao foi possivel autenticar no momento.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateInputs = () => {
    const identifier = document.getElementById('identifier');
    const password = document.getElementById('password');

    let isValid = true;

    if (!identifier.value || !String(identifier.value).trim()) {
      setIdentifierError(true);
      setIdentifierErrorMessage('Por favor, insira seu email.');
      isValid = false;
    } else {
      setIdentifierError(false);
      setIdentifierErrorMessage('');
    }

    if (!password.value || password.value.length < 8) {
      setPasswordError(true);
      setPasswordErrorMessage('A senha deve ter no mínimo 8 caracteres.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="center">
        <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem' }} />
        <Card variant="outlined">
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: { xs: '1.75rem', sm: '2.15rem' } }}
          >
            Entrar
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="identifier">Email</FormLabel>
              <TextField
                error={identifierError}
                helperText={identifierErrorMessage}
                id="identifier"
                type="text"
                name="identifier"
                placeholder="ex.: wagner123"
                autoComplete="username"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={identifierError ? 'error' : 'primary'}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">Senha</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={passwordError ? 'error' : 'primary'}
              />
            </FormControl>
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Lembrar-me"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Entrando...' : 'Entrar'}
            </Button>
          </Box>
        </Card>
      </SignInContainer>
    </AppTheme>
  );
}
